import React from 'react'
import { Controller } from 'react-hook-form'
import { Element } from '@saas/interfaces/graphql'
import PatientFormField from '../PatientFormField'
import ChipField from '../../../Common/ChipField'
import { usePatientFormContext } from '../../context/PatientFormContext'
import { shouldDirty } from '../PatientIdentity/_utils/_utils'
import { patientFormErrors } from '../../_errors'
import { getFirstNamesValues, useFormContextPatient } from '../_utils'
import { cleanAccentsAndDiacritics } from '../../../../utils/stringUtils'

const FirstNameList = () => {
    const { control, setValue, setError, clearErrors, watch } = useFormContextPatient()

    const watchedFirstFirstName = watch('firstFirstName') ?? undefined

    const {
        state: { isDisabled },
    } = usePatientFormContext()

    return (
        <Controller
            control={control}
            name="firstNameList"
            rules={{
                required: patientFormErrors.firstNameList?.required,
                validate: (val) => Boolean((val?.length ?? 0) > 0),
            }}
            render={({ field, fieldState: { error } }) => {
                const values = field?.value
                    ?.filter((item) => item?.__typename === 'Element' || typeof item === 'string')
                    .map((item) => {
                        if (item?.__typename === 'Element') {
                            return cleanAccentsAndDiacritics(item?.value)?.toUpperCase()
                        }
                        if (typeof item === 'string') {
                            return cleanAccentsAndDiacritics(String(item)?.toUpperCase() ?? '')
                        }
                        return undefined
                    })

                const handleChange = (firstNames: Element[]) => {
                    const firstNamesValues = getFirstNamesValues(firstNames)

                    const firstFirstName = firstNamesValues[0]

                    setValue('firstNameList', firstNames, shouldDirty)

                    if (firstNames.length === 0) {
                        setValue('firstFirstName', '', shouldDirty)
                    }

                    if (watchedFirstFirstName && !firstNamesValues.includes(watchedFirstFirstName)) {
                        setValue('firstFirstName', firstFirstName ?? '', shouldDirty)
                    }

                    if (!watchedFirstFirstName) {
                        setValue('firstFirstName', firstFirstName ?? '', shouldDirty)
                    }

                    clearErrors('firstNameList')
                }

                return (
                    <PatientFormField
                        variant="custom"
                        label="Prénom(s) de naissance"
                        name={field.name}
                        disabled={isDisabled}
                        status={{
                            error,
                            valid: Boolean((field.value?.length ?? 0) > 0),
                        }}
                        required
                        render={() => (
                            <ChipField
                                values={values}
                                dataCy={`dataCy_PatientField-${field.name}`}
                                onDelete={(e) => {
                                    const cleared = field.value?.reduce<Element[]>((prev, curr) => {
                                        if (
                                            curr?.__typename === 'Element' &&
                                            curr?.value?.toUpperCase() !== e.toUpperCase()
                                        ) {
                                            prev.push(curr)
                                        } else if (
                                            typeof curr === 'string' &&
                                            curr?.toUpperCase() !== e.toUpperCase()
                                        ) {
                                            prev.push({
                                                __typename: 'Element',
                                                value: curr,
                                            })
                                        }

                                        return prev
                                    }, [])

                                    handleChange(cleared ?? [])

                                    if ((cleared?.length ?? 0) === 0) {
                                        setError('firstNameList', {
                                            message: `Le prénom de naissance est obligatoire et n'a pas été renseigné`,
                                        })
                                    }
                                }}
                                onChange={(e) => {
                                    const firstnames =
                                        field.value?.reduce<Element[]>((prev, curr) => {
                                            if (curr?.__typename === 'Element') {
                                                prev.push(curr)
                                            } else if (typeof curr === 'string') {
                                                prev.push({
                                                    __typename: 'Element',
                                                    value: curr,
                                                })
                                            }
                                            return prev
                                        }, []) ?? []

                                    firstnames.push({
                                        __typename: 'Element',
                                        value: e,
                                    })
                                    handleChange(firstnames)
                                }}
                                dialogTitle="Ajouter un prénom de naissance"
                                dialogSubtitle="Saisissez d’abord le 1er prénom de naissance, puis les autres éventuels prénoms naissance"
                                disabled={isDisabled}
                                hasError={!!error}
                                placeholder="Cliquer pour ajouter un prénom"
                                textFieldValueStyle="uppercase"
                            />
                        )}
                    />
                )
            }}
        />
    )
}

export default FirstNameList
